// 新しいバージョンのデプロイを検知して再読み込みを促すための判定。
//
// なぜ必要か:
// 開いたままの画面は、読み込み直すまでビルド時点の古いプログラムで動き続ける。
// 特に iPhone/Android のホーム画面から起動したアプリ（manifest.json の display:standalone）は
// 再読み込みの手段がなく、切り替えても一時停止から再開されるだけなので、何日も古い版のまま
// 使われうる。古い画面が新しいサーバー側の処理を呼ぶ組み合わせのずれも起こる。
//
// 画面に埋め込んだビルド時のコミット（REACT_APP_COMMIT_SHA、package.json の build で
// VERCEL_GIT_COMMIT_SHA から渡す）と、/api/version が返す現在のデプロイのコミットを比べる。
//
// 動作確認の方法: ブランチの固定URL（常にそのブランチの最新デプロイを指す）を開いたまま、
// 同じブランチに別のコミットを push し、デプロイ完了後にアプリを前面に戻す。
// 同じデプロイを開いている間は帯が出ないのが正しい動き。
// 何も開いていない最初の画面なら帯を出さずに自動で読み込み直し、設定・履歴等を
// 開いていれば帯を出す（isSafeToAutoReload）。
// 姉妹アプリ food-label-assist の src/utils/updateCheck.js と同じ実装（FLA v5.0.16 で導入し、
// Android・iPhone のホーム画面アプリで確認済み）。片方を直すときはもう片方も直すこと
// PM では v3.10.14 で導入し、ホーム画面アプリで帯・2回押しを確認した。

// 前面に戻ったとき以外の定期確認の間隔。PCで同じタブを表に出したまま一日中使う場合の対策
export const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000;

// 確認の最小間隔。アプリの切り替えを繰り返したときに /api/version を連打しないため
export const UPDATE_CHECK_MIN_GAP_MS = 60 * 1000;

// ビルド時のコミットとサーバーのコミットが両方わかっていて、異なる場合だけ true。
// どちらかが不明（ローカル開発でビルド時のコミットが無い、APIが失敗した等）なら通知しない。
// 誤って通知すると、利用者が再読み込みしても帯が消えない状態になるため
export function isNewDeployment(buildCommit, serverCommit) {
  const b = typeof buildCommit === 'string' ? buildCommit.trim() : '';
  const s = typeof serverCommit === 'string' ? serverCommit.trim() : '';
  if (!b || !s) return false;
  return b !== s;
}

// 直前に操作があった場合は自動で読み込み直さない時間。
// 撮影・写真選択の画面から戻った直後は、選んだ写真の圧縮がまだ終わっておらず画面上は
// 「何も無い」ように見える。画面の状態の一覧だけに頼ると、このような一時的な状態や、
// 今後追加される画面の考慮漏れで入力が消えるため、操作の直後はそれだけで対象外にする
export const AUTO_RELOAD_QUIET_MS = 2 * 60 * 1000;

// 前面に戻ったときに自動で読み込み直してよい状態か。
// 撮影した写真・解析結果・入力途中のフォーム等、読み込み直すと消えるものが画面に
// 一つでもあるか、直前（AUTO_RELOAD_QUIET_MS 以内）に操作があれば false
// （その場合は帯とボタンで利用者に委ねる）
export function isSafeToAutoReload(state) {
  if (!state) return false;
  return (state.itemCount ?? 1) === 0
    && (state.sessionImageCount ?? 1) === 0
    && (state.batchImageCount ?? 1) === 0
    && !state.busy
    && !state.overlayOpen
    && (state.msSinceInteraction ?? 0) >= AUTO_RELOAD_QUIET_MS;
}
